import React from 'react';
import {
    Box, Avatar, Typography, Menu, Button, Dialog, DialogTitle, DialogContent,
    DialogActions, TextField
} from '@mui/material';
import { useAuthContext } from '../context/AuthContext';

interface UserMenuProps {
    username: string;
    anchorEl: HTMLElement | null;
    handleMenu: (event: React.MouseEvent<HTMLElement>) => void;
    handleClose: () => void;
    handleSignOut: (event: React.FormEvent<HTMLFormElement>) => void;
}

// Lets the signed in user change their own password
const ChangePasswordDialog: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
    const { changePassword } = useAuthContext();
    const [current, setCurrent] = React.useState("");
    const [next, setNext] = React.useState("");
    const [confirm, setConfirm] = React.useState("");
    const [message, setMessage] = React.useState("");
    const [done, setDone] = React.useState(false);
    const [busy, setBusy] = React.useState(false);

    const close = () => {
        setCurrent(""); setNext(""); setConfirm(""); setMessage(""); setDone(false);
        onClose();
    };

    const submit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (current === "" || next === "") { setMessage("Please fill in every field"); return; }
        if (next !== confirm) { setMessage("The two new passwords do not match"); return; }
        setBusy(true);
        setMessage("");
        const error = await changePassword(current, next);
        setBusy(false);
        if (error) setMessage(error);
        else setDone(true);
    };

    return (
        <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
            <DialogTitle>Change password</DialogTitle>
            {done ? (
                <>
                    <DialogContent>
                        <Typography variant="body1">Your password has been changed.</Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={close} variant="contained">Close</Button>
                    </DialogActions>
                </>
            ) : (
                <Box component="form" onSubmit={submit} noValidate>
                    <DialogContent>
                        <TextField label="Current password" type="password" fullWidth autoFocus
                            value={current} onChange={(e) => setCurrent(e.currentTarget.value)}
                            autoComplete="current-password" sx={{ mt: 1, mb: 1 }} />
                        <TextField label="New password" type="password" fullWidth
                            value={next} onChange={(e) => setNext(e.currentTarget.value)}
                            autoComplete="new-password" sx={{ mt: 1, mb: 1 }} />
                        <TextField label="Confirm new password" type="password" fullWidth
                            value={confirm} onChange={(e) => setConfirm(e.currentTarget.value)}
                            autoComplete="new-password" sx={{ mt: 1, mb: 1 }} />
                        <Typography variant="body2" sx={{ minHeight: "24px", color: "red" }}>
                            {message}
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={close} disabled={busy}>Cancel</Button>
                        <Button type="submit" variant="contained" disabled={busy}>Change password</Button>
                    </DialogActions>
                </Box>
            )}
        </Dialog>
    );
};

const UserMenu: React.FC<UserMenuProps> = ({ username, anchorEl, handleMenu, handleClose, handleSignOut }) => {
    const [changePasswordOpen, setChangePasswordOpen] = React.useState(false);
    return (
        <>
            <Button color="inherit" onClick={handleMenu} sx={{ borderRadius: 0, textTransform: "none" }} >
                <Box display="flex" alignItems="center">
                    <Avatar alt={username} src="/static/images/avatar/1.jpg" />
                    <Typography variant="body1" color="inherit" style={{ marginLeft: 8 }}>
                        {username}
                    </Typography>
                </Box>
            </Button>
            <Menu
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
            >
                <Typography variant="body1" align="center" sx={{ p: 1 }}>
                    {"You are logged in as " + username}
                </Typography>
                <Button fullWidth onClick={() => { handleClose(); setChangePasswordOpen(true); }}
                    sx={{ height: '45px', borderRadius: 0 }}>
                    Change password
                </Button>
                <Box component="form" width={400} onSubmit={handleSignOut} noValidate sx={{ p: 0 }}>
                    <Button type="submit" fullWidth variant="contained" sx={{ mt: 1, mb: 1, height: '45px', borderRadius: 0 }}>
                        Sign Out
                    </Button>
                </Box>
            </Menu>
            <ChangePasswordDialog open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} />
        </>
    );
};

export default UserMenu;